import { useState, useEffect, useCallback, useRef, useReducer } from "react";
import { WebRTCConnection } from "../models";
import type {
  WebRTCConfig,
  StreamingCallbacks,
  StreamingConnectionState,
  SignalingConfig,
  ConnectionError,
} from "../types";
import { MediaStreamId, SignalingStatus } from "../constants/webrtc";

interface UseWebRTCOptions extends WebRTCConfig {
  callbacks?: StreamingCallbacks;
  videoElement?: HTMLVideoElement | null;
  audioElement?: HTMLAudioElement | null;
  signalingConfig?: SignalingConfig;
  mediaStreamId?: MediaStreamId;
  turnConfiguration?: RTCConfiguration;
  isTV?: boolean;
}

export const useWebRTC = ({
  callbacks,
  videoElement,
  audioElement,
  signalingConfig,
  mediaStreamId,
  turnConfiguration,
  isTV,
}: UseWebRTCOptions) => {
  const [connectionState, setConnectionState] =
    useState<StreamingConnectionState>({
      connectionState: "new",
      isConnected: false,
      hasRemoteStream: false,
    });

  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [signalingStatus, setSignalingStatus] = useState<SignalingStatus>(
    SignalingStatus.DISCONNECTED,
  );

  const connectionRef = useRef<WebRTCConnection | null>(null);
  const [, bumpConnectionVersion] = useReducer((n: number) => n + 1, 0);

  const onConnectionStateChange = useCallback(
    (state: StreamingConnectionState) => {
      setConnectionState(state);
      callbacks?.onConnectionStateChange?.(state);
    },
    [callbacks],
  );

  const onChangeSignalingStatus = (status: SignalingStatus) => {
    setSignalingStatus(status);
  };

  const onStreamReceived = useCallback(
    (stream: MediaStream) => {
      setRemoteStream(stream);
      callbacks?.onStreamReceived?.(stream);
    },
    [callbacks],
  );

  const onError = useCallback(
    (err: ConnectionError) => {
      setError(err.message);
      callbacks?.onError?.(err);
    },
    [callbacks],
  );

  useEffect(() => {
    if (signalingConfig && mediaStreamId && turnConfiguration) {
      if (
        (mediaStreamId === MediaStreamId.Video && !videoElement) ||
        (mediaStreamId === MediaStreamId.Audio && !audioElement)
      ) {
        return;
      }

      const streamingCallbacks: StreamingCallbacks = {
        ...callbacks,
        onStreamReceived,
        onError,
        onConnectionStateChange,
        onChangeSignalingStatus,
      };

      connectionRef.current = new WebRTCConnection({
        callbacks: streamingCallbacks,
        videoElement: videoElement || undefined,
        audioElement: audioElement || undefined,
        rtcConfiguration: turnConfiguration,
        signalingConfig,
        mediaStreamId,
        isTV,
      });
      bumpConnectionVersion();
    }
  }, [
    videoElement,
    signalingConfig?.url,
    mediaStreamId,
    audioElement,
    turnConfiguration,
  ]);

  const connect = useCallback(async () => {
    if (!connectionRef.current || !turnConfiguration) {
      console.warn("Missing required configuration for connection");
      return;
    }

    connectionRef.current.setRtcPeerConfiguration(turnConfiguration);
    connectionRef.current.isDisconnected = false;
    await connectionRef.current.connect();
  }, [turnConfiguration]);

  const disconnect = useCallback(() => {
    if (connectionRef.current) {
      connectionRef.current.disconnect();
    }
  }, []);

  const cleanup = () => {
    if (remoteStream) {
      setRemoteStream(null);
    }

    if (connectionRef.current) {
      connectionRef.current.isDisconnected = true;
      connectionRef.current.cleanup();
    }
  };

  const reset = useCallback(async () => {
    if (connectionRef.current) {
      connectionRef.current.isDisconnected = false;
      await connectionRef.current.reset();
    }
  }, []);

  return {
    connectionState,
    remoteStream,
    error,
    signalingStatus,
    connect,
    disconnect,
    cleanup,
    reset,
    connection: connectionRef.current,
  };
};
